import React, { useMemo } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Building2,
  Users,
  Wallet,
  Landmark,
  HandHeart,
  Target,
  ArrowRight,
  TrendingUp,
} from "lucide-react";
import unaData from "./data/una-nonprofits.json";
import Header from "../components/Header";
import Footer from "../components/Footer";

// --- Design Configuration ---
// --- Design Configuration ---
const COLORS = {
  primary: "#CC0000", // Official Red
  secondary: "#0B1220", // Deep Ink
  text: "#333333",
  muted: "#6B7280",
  background: "#F9FAFB",
  white: "#FFFFFF",
  chartColors: ["#D90429", "#2B2D42", "#8D99AE", "#9467bd", "#9ae3ecd7", "#1f77b4", "#ff7f0e"],
  extendedColors: [
    "#CC0000", "#1f77b4", "#ff7f0e", "#2ca02c", "#9467bd",
    "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"
  ],
};

// Animation Variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const cardHover = {
  rest: { y: 0, boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)" },
  hover: {
    y: -5,
    boxShadow: "0px 10px 15px rgba(0, 0, 0, 0.1)",
    transition: { type: "spring", stiffness: 300 },
  },
};

const UtahNonprofits = () => {
  const { overview, irs_classification_breakdown, revenue_distribution, county_distribution, top_causes_by_revenue, funding_sources, data_sources } = unaData;

  // Data Preparation
  const irsData = useMemo(() => irs_classification_breakdown.map(item => ({
    name: `${item.irs_section} - ${item.type}`, value: item.count, fullType: item.type
  })), [irs_classification_breakdown]);

  const fundingData = useMemo(() => funding_sources.map(item => ({
    name: item.source, value: item.share
  })), [funding_sources]);

  const revenueData = useMemo(() => revenue_distribution.map(item => ({
    name: item.range, percentage: item.percentage
  })), [revenue_distribution]);

  // Normalize Top Causes to Billions
  const normalizedTopCauses = useMemo(() => {
    return top_causes_by_revenue.map(item => {
      let revenue = 0;
      if (item.revenue_usd_billion) {
        revenue = item.revenue_usd_billion;
      } else if (item.revenue_usd_million) {
        revenue = item.revenue_usd_million / 1000;
      }
      return {
        ...item,
        revenue_billions: revenue
      };
    });
  }, [top_causes_by_revenue]);

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return percent > 0.05 ? (
      <text x={x} y={y} fill="white" fontSize={12} fontWeight="bold" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) : null;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-50 to-white" style={{ color: COLORS.text, minHeight: "100vh", }}>
      <Header />
      {/* --- HERO SECTION --- */}
      <div style={{
        background: `linear-gradient(135deg, rgba(117, 7, 7, 0.85) 0%, rgba(30, 41, 59, 0.95) 100%), url(/img/landing/service.webp)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "white",
        padding: "5rem 0 8rem 0",
        clipPath: "polygon(0 0, 100% 0, 100% 85%, 0 100%)"
      }}>
        <Container>
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <Row className="align-items-center">
              <Col lg={8} className="mx-auto text-center">
                <Badge bg="light" text="dark" className="mb-3 px-3 py-2 text-uppercase fw-bold rounded-pill">
                  2025 Impact Report
                </Badge>
                <h1 className="display-3 fw-bold mb-3">Utah's Nonprofit Sector</h1>
                <p className="lead opacity-75 mb-4 px-5">
                  The heartbeat of our communities: driving economic growth, social innovation, and public welfare across the Beehive State.
                </p>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </div>

      <Container style={{ marginTop: "-5rem" }}>

        {/* --- KPI STATS GRID --- */}
        <motion.div initial="hidden" animate="visible" variants={fadeIn}>
          <Row className="g-4 mb-5">
            {[
              { label: "Active Organizations", value: overview.active_tax_exempt_entities.toLocaleString(), icon: Building2, color: "#4F46E5" },
              { label: "Employees", value: overview.nonprofit_employment.toLocaleString(), icon: Users, color: "#10B981" },
              { label: "Total Assets", value: `$${overview.total_assets_usd_billion}B`, icon: Landmark, color: "#F59E0B" },
              { label: "Annual Revenue", value: `$${overview.total_revenue_usd_billion}B`, icon: Wallet, color: "#EF4444" },
            ].map((stat, idx) => (
              <Col md={3} key={idx}>
                <motion.div variants={cardHover} initial="rest" whileHover="hover">
                  <Card className="h-100 border-0 rounded-4 overflow-hidden" style={{ background: "white" }}>
                    <Card.Body className="p-4 position-relative">
                      <div className="d-flex align-items-center mb-3">
                        <div className="p-3 rounded-3" style={{ backgroundColor: `${stat.color}15` }}>
                          <stat.icon size={24} color={stat.color} />
                        </div>
                      </div>
                      <h2 className="fw-bold mb-1">{stat.value}</h2>
                      <div className="text-muted small fw-bold text-uppercase">{stat.label}</div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>

        {/* --- MAIN DASHBOARD CONTENT --- */}
        <Row className="g-4 mb-5">
          {/* LEFT COL: Sector Composition */}
          <Col lg={8}>
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <Card className="border-0 shadow-sm rounded-4 h-100">
                <Card.Header className="bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="fw-bold m-0 d-flex align-items-center gap-2">
                      <HandHeart size={20} className="text-secondary" /> Sector Composition
                    </h5>
                    <small className="text-muted">Breakdown by IRS Designation</small>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={7} style={{ height: 325 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={irsData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={110}
                            paddingAngle={2}
                            dataKey="value"
                            stroke="none"
                          >
                            {irsData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS.chartColors[index % COLORS.chartColors.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            formatter={(value) => value.toLocaleString()}
                          />
                          <Legend layout="horizontal" verticalAlign="bottom" align="left" wrapperStyle={{ fontSize: '12px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Col>
                    <Col md={5}>
                      <div className="p-3 bg-light rounded-3">
                        <h6 className="fw-bold mb-3">Key Takeaways</h6>
                        <ul className="list-unstyled small text-muted mb-0 d-flex flex-column gap-2">
                          <li className="d-flex align-items-start gap-2">
                            <div className="mt-1 rounded-circle" style={{ width: 6, height: 6, background: COLORS.primary }} />
                            <span><strong>85.4%</strong> are 501(c)(3) charities serving religious, educational, and charitable needs.</span>
                          </li>
                          <li className="d-flex align-items-start gap-2">
                            <div className="mt-1 rounded-circle" style={{ width: 6, height: 6, background: COLORS.secondary }} />
                            <span>Small but vital presence of Social Welfare & Business Leagues.</span>
                          </li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>

          {/* RIGHT COL: Funding Sources */}
          <Col lg={4}>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
              <Card className="border-0 shadow-sm rounded-4 h-100">
                <Card.Header className="bg-white border-0 pt-4 px-4">
                  <h5 className="fw-bold m-0 d-flex align-items-center gap-2">
                    <TrendingUp size={20} className="text-secondary" /> Funding Sources
                  </h5>
                </Card.Header>
                <Card.Body className="d-flex flex-column justify-content-center">
                  <div style={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={fundingData}
                          cx="50%"
                          cy="45%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="value"
                          stroke="none"
                        >
                          {fundingData.map((entry, index) => (
                            <Cell key={index} fill={COLORS.extendedColors[index % COLORS.extendedColors.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(val) => `${val}%`} />
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ fontSize: '11px', lineHeight: '14px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>

        {/* --- ROW 2: BAR CHARTS --- */}
        <Row className="g-4 mb-5">
          <Col lg={7}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Header className="bg-white border-0 pt-4 px-4">
                <h5 className="fw-bold">Leading Cause Areas by Revenue</h5>
                <p className="text-muted small mb-0">Where the financial resources are concentrated</p>
              </Card.Header>
              <Card.Body>
                <div style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={normalizedTopCauses} margin={{ top: 20, right: 30, left: 10, bottom: 5 }} barSize={40}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis
                        dataKey="cause"
                        interval={0}
                        height={60}
                        tick={({ x, y, payload }) => (
                          <g transform={`translate(${x},${y})`}>
                            <text x={0} y={10} dy={16} textAnchor="middle" fill="#9ca3af" fontSize={11}>
                              {payload.value.split(" ").reduce((acc, word) => {
                                const lastLine = acc[acc.length - 1];
                                if (lastLine && (lastLine + " " + word).length < 15) {
                                  acc[acc.length - 1] = lastLine + " " + word;
                                } else {
                                  acc.push(word);
                                }
                                return acc;
                              }, []).map((line, i) => (
                                <tspan key={i} x={0} dy={i === 0 ? 0 : 12}>
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          </g>
                        )}
                      />
                      <YAxis
                        dataKey="revenue_billions"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12 }}
                        stroke="#9ca3af"
                        width={40}
                        tickMargin={5}
                        label={{ value: 'Billions (USD)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#9ca3af' }, dx: 10 }}
                      />
                      <Tooltip
                        cursor={{ fill: 'transparent' }}
                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        formatter={(val) => [`$${val.toFixed(2)}B`, "Revenue"]}
                      />
                      <Bar dataKey="revenue_billions" radius={[4, 4, 0, 0]}>
                        {normalizedTopCauses.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.extendedColors[index % COLORS.extendedColors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={5}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Header className="bg-white border-0 pt-4 px-4">
                <h5 className="fw-bold">Organization Size</h5>
                <p className="text-muted small mb-0">Distribution by Annual Revenue</p>
              </Card.Header>
              <Card.Body>
                <div style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={revenueData} margin={{ top: 5, right: 30, left: 40, bottom: 5 }} barSize={20}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                      <XAxis type="number" unit="%" hide />
                      <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11 }} stroke="#6b7280" tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: '#f3f4f6' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      <Bar dataKey="percentage" radius={[0, 4, 4, 0]}>
                        {revenueData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.extendedColors[index % COLORS.extendedColors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* --- GEOGRAPHIC & SOURCES --- */}
        <Row className="g-4 mb-5">
          <Col xs={12}>
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
              <Card.Header className="bg-white border-0 pt-4 px-4 pb-3">
                <div className="d-flex justify-content-between align-items-end">
                  <div>
                    <h5 className="fw-bold m-0"><Target size={20} className="me-2 text-primary" />Geographic Distribution</h5>
                    <p className="text-muted small mb-0 mt-1">Nonprofit density across main counties</p>
                  </div>
                  <Badge bg="light" text="dark" className="border">Top 7 Counties</Badge>
                </div>
              </Card.Header>
              <Table responsive hover className="mb-0 text-nowrap align-middle">
                <thead className="bg-light text-muted small text-uppercase">
                  <tr>
                    <th className="ps-4 border-0">County</th>
                    <th className="border-0">Total Orgs</th>
                    <th className="border-0" style={{ width: '40%' }}>Concentration</th>
                    <th className="border-0">Key Hubs</th>
                  </tr>
                </thead>
                <tbody>
                  {county_distribution.map((county, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td className="ps-4 py-3 fw-bold">{county.county}</td>
                      <td className="text-secondary fw-semibold">{county.nonprofits.toLocaleString()}</td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div className="progress flex-grow-1" style={{ height: '6px', borderRadius: '10px', backgroundColor: '#e5e7eb' }}>
                            <div
                              className="progress-bar"
                              style={{
                                width: `${(county.nonprofits / 5202) * 100}%`,
                                backgroundColor: idx === 0 ? COLORS.primary : COLORS.secondary,
                                borderRadius: '10px'
                              }}
                            ></div>
                          </div>
                          <small className="text-muted">{((county.nonprofits / 13614) * 100).toFixed(1)}%</small>
                        </div>
                      </td>
                      <td>
                        {county.key_hubs.map((hub, i) => (
                          <Badge key={i} bg="white" text="dark" className="me-1 border fw-normal shadow-sm">{hub}</Badge>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>

        <div className="text-center pb-5">
          <p className="text-muted small mb-2">Data Sources</p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            {data_sources.map((source, idx) => (
              <a key={idx} href={source} target="_blank" rel="noopener noreferrer" className="text-decoration-none small text-secondary fw-bold bg-white px-3 py-1 rounded-pill shadow-sm border border-light transition-all hover-shadow">
                {new URL(source).hostname.replace('www.', '')} <ArrowRight size={12} className="ms-1" />
              </a>
            ))}
          </div>
        </div>

      </Container>
      <Footer />
    </div>
  );
};

export default UtahNonprofits;
